import {
  ArrowLeftRight,
  Building2,
  Check,
  ChevronDown,
  Compass,
  HelpCircle,
  LogOut,
  MapPinIcon,
  Menu,
  Search,
  Settings,
  Users,
  X,
} from "lucide-react";
import { forwardRef, useMemo, useState } from "react";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { formatWhitelabelName } from "@/utils/utils";
import { Avatar, AvatarFallback } from "@/v2/ui/primitives/avatar";
import { Button } from "@/v2/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/v2/ui/primitives/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/v2/ui/primitives/popover";
import { ScrollArea } from "@/v2/ui/primitives/scroll-area";

export interface City {
  id: number;
  name: string;
}

export interface Whitelabel {
  id: number;
  name: string;
  icon?: string;
}

export interface Partner {
  id: number;
  name: string;
}

export interface NavbarProps {
  logo?: string;
  logoAlt?: string;
  email?: string;
  cities?: City[];
  selectedCity?: City | null;
  onCityChange?: (city: City | null) => void;
  whitelabels?: Whitelabel[];
  selectedWhitelabel?: Whitelabel | null;
  onWhitelabelChange?: (whitelabel: Whitelabel | null) => void;
  showWhitelabelSelector?: boolean;
  partners?: Partner[];
  selectedPartner?: Partner | null;
  onPartnerChange?: (partner: Partner | null) => void;
  showPartnerSelector?: boolean;
  onLogout?: () => void;
  onSettingsClick?: () => void;
  onLogoClick?: () => void;
  onHelpClick?: () => void;
  onMenuClick?: () => void;
  onVersionToggle?: () => void;
  onTourClick?: () => void;
  versionLabel?: string;
  showCitySelector?: boolean;
  backgroundColor?: string;
  textColor?: string;
  className?: string;
}

const cityDisplayName = (city?: City) => city?.name || "(no city name)";

const Navbar = forwardRef<HTMLDivElement, NavbarProps>(
  (
    {
      logo,
      logoAlt = strings("navbar.logoAlt"),
      email,
      cities = [],
      selectedCity,
      onCityChange,
      whitelabels = [],
      selectedWhitelabel,
      onWhitelabelChange,
      showWhitelabelSelector = false,
      partners = [],
      selectedPartner,
      onPartnerChange,
      showPartnerSelector = false,
      onLogout,
      onSettingsClick,
      onLogoClick,
      onHelpClick,
      onMenuClick,
      onVersionToggle,
      onTourClick,
      versionLabel,
      showCitySelector = true,
      backgroundColor,
      textColor,
      className,
    },
    ref
  ) => {
    const [citySearch, setCitySearch] = useState("");
    const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
    const [whitelabelSearch, setWhitelabelSearch] = useState("");
    const [whitelabelDropdownOpen, setWhitelabelDropdownOpen] = useState(false);
    const [partnerSearch, setPartnerSearch] = useState("");
    const [partnerDropdownOpen, setPartnerDropdownOpen] = useState(false);

    const citySelectorClassName =
      cities.length > 0
        ? "tw-cursor-pointer hover:tw-bg-white/25 lg:hover:tw-bg-white/10"
        : "tw-opacity-50 tw-cursor-not-allowed";

    const sortedCities = useMemo(() => {
      return [...cities].sort((a, b) =>
        cityDisplayName(a).localeCompare(cityDisplayName(b))
      );
    }, [cities]);

    const filteredCities = useMemo(() => {
      if (!citySearch.trim()) return sortedCities;
      const search = citySearch.toLowerCase();
      return sortedCities.filter((city) =>
        cityDisplayName(city).toLowerCase().includes(search)
      );
    }, [sortedCities, citySearch]);

    const sortedWhitelabels = useMemo(() => {
      return [...whitelabels].sort((a, b) => a.name.localeCompare(b.name));
    }, [whitelabels]);

    const filteredWhitelabels = useMemo(() => {
      if (!whitelabelSearch.trim()) return sortedWhitelabels;
      const search = whitelabelSearch.toLowerCase();
      return sortedWhitelabels.filter((wl) =>
        wl.name.toLowerCase().includes(search)
      );
    }, [sortedWhitelabels, whitelabelSearch]);

    const sortedPartners = useMemo(() => {
      return [...partners].sort((a, b) => a.name.localeCompare(b.name));
    }, [partners]);

    const filteredPartners = useMemo(() => {
      if (!partnerSearch.trim()) return sortedPartners;
      const search = partnerSearch.toLowerCase();
      return sortedPartners.filter((p) =>
        p.name.toLowerCase().includes(search)
      );
    }, [sortedPartners, partnerSearch]);

    const handleCitySelect = (city: City | null) => {
      onCityChange?.(city);
      setCityDropdownOpen(false);
      setCitySearch("");
    };

    const handleWhitelabelSelect = (whitelabel: Whitelabel | null) => {
      onWhitelabelChange?.(whitelabel);
      setWhitelabelDropdownOpen(false);
      setWhitelabelSearch("");
    };

    const handlePartnerSelect = (partner: Partner | null) => {
      onPartnerChange?.(partner);
      setPartnerDropdownOpen(false);
      setPartnerSearch("");
    };

    const getInitials = (email?: string) => {
      if (!email) return "?";
      const parts = email.split("@")[0].split(/[._-]/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return email.slice(0, 2).toUpperCase();
    };

    return (
      <header
        ref={ref}
        className={cn(
          "tw-flex tw-h-16 tw-items-center tw-justify-between tw-px-2 lg:tw-px-4 tw-shadow-sm tw-bg-[--navbar-bg] tw-text-[--navbar-text]",
          className
        )}
        style={
          {
            "--navbar-bg": backgroundColor || "hsl(var(--primary))",
            "--navbar-text": textColor || "hsl(var(--primary-foreground))",
          } as React.CSSProperties
        }
      >
        <div className="tw-flex tw-items-center tw-gap-3">
          {onMenuClick && (
            <Button
              variant="none"
              size="icon"
              onClick={onMenuClick}
              className="tw-hidden md:tw-flex lg:tw-hidden tw-cursor-pointer hover:tw-bg-white/10 tw-text-[--navbar-text] focus:tw-outline-none focus-visible:tw-ring-0"
              aria-label={strings("navbar.openMenu")}
            >
              <Menu className="tw-h-5 tw-w-5" />
            </Button>
          )}
          {logo && (
            <button
              type="button"
              onClick={onLogoClick}
              className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0"
            >
              <img
                src={logo}
                alt={logoAlt}
                className="tw-h-8 lg:tw-h-10 tw-w-auto tw-object-contain"
              />
            </button>
          )}
          {onVersionToggle && (
            <Button
              variant="none"
              onClick={onVersionToggle}
              aria-label={versionLabel || strings("navbar.switchUi")}
              className="tw-hidden md:tw-flex tw-items-center tw-gap-2 tw-px-0 lg:tw-px-4 tw-py-1.5 tw-h-11 tw-w-11 lg:tw-w-auto lg:tw-h-auto tw-bg-white/15 hover:tw-bg-white/25 tw-border tw-border-white/30 tw-rounded-full tw-cursor-pointer tw-transition-colors tw-text-[--navbar-text]"
            >
              <ArrowLeftRight className="tw-h-4 tw-w-4" />
              <span className="tw-hidden lg:tw-inline tw-text-sm tw-font-medium">
                {versionLabel || strings("navbar.switchUi")}
              </span>
            </Button>
          )}
        </div>

        <div className="tw-flex tw-items-center tw-gap-2 lg:tw-gap-3">
          {/* Whitelabel selector */}
          {showWhitelabelSelector && whitelabels.length > 0 && (
            <Popover
              open={whitelabelDropdownOpen}
              onOpenChange={setWhitelabelDropdownOpen}
            >
              <PopoverTrigger asChild={true}>
                <Button
                  variant="none"
                  size="icon"
                  role="combobox"
                  aria-expanded={whitelabelDropdownOpen}
                  data-tour="whitelabel-selector"
                  className={cn(
                    "tw-cursor-pointer tw-text-[--navbar-text] focus:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0",
                    "tw-h-11 tw-w-11 tw-rounded-full tw-bg-white/15 hover:tw-bg-white/25 tw-border-0",
                    "lg:tw-h-auto lg:tw-w-[180px] lg:tw-px-4 lg:tw-py-2 lg:tw-rounded-lg lg:tw-bg-transparent lg:tw-border lg:tw-border-[--navbar-border] lg:hover:tw-bg-white/10 lg:tw-justify-between"
                  )}
                  style={
                    {
                      "--navbar-border": `${textColor}40`,
                    } as React.CSSProperties
                  }
                  aria-label={
                    selectedWhitelabel?.name
                      ? formatWhitelabelName(selectedWhitelabel.name)
                      : strings("navbar.allWhitelabels")
                  }
                >
                  {/* Mobile: icon only */}
                  <span className="lg:tw-hidden tw-flex tw-items-center tw-justify-center">
                    {selectedWhitelabel?.icon ? (
                      <img
                        src={selectedWhitelabel.icon}
                        alt=""
                        className="tw-w-4 tw-h-4 tw-rounded"
                      />
                    ) : (
                      <Building2 className="tw-h-4 tw-w-4" />
                    )}
                  </span>
                  {/* Desktop: full label */}
                  <span className="tw-hidden lg:tw-flex tw-items-center tw-gap-2 tw-truncate tw-text-base">
                    {selectedWhitelabel?.icon && (
                      <img
                        src={selectedWhitelabel.icon}
                        alt=""
                        className="tw-w-5 tw-h-5 tw-rounded tw-flex-shrink-0"
                      />
                    )}
                    <span className="tw-truncate">
                      {selectedWhitelabel?.name
                        ? formatWhitelabelName(selectedWhitelabel.name)
                        : strings("navbar.allWhitelabels")}
                    </span>
                  </span>
                  <ChevronDown className="tw-hidden lg:tw-block tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="tw-w-[calc(100vw-32px)] sm:tw-w-[250px] tw-p-0"
                align="end"
              >
                <div className="tw-flex tw-items-center tw-border-b tw-px-3 tw-py-2">
                  <Search className="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                  <input
                    placeholder={strings("placeholders.searchWhiteLabel")}
                    value={whitelabelSearch}
                    onChange={(e) => setWhitelabelSearch(e.target.value)}
                    className="tw-flex tw-h-8 tw-w-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-muted-foreground"
                  />
                  {whitelabelSearch && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="tw-h-6 tw-w-6"
                      onClick={() => setWhitelabelSearch("")}
                    >
                      <X className="tw-h-3 tw-w-3" />
                    </Button>
                  )}
                </div>
                <ScrollArea className="tw-h-[300px]">
                  <div className="tw-p-1">
                    <button
                      type="button"
                      onClick={() => handleWhitelabelSelect(null)}
                      className={cn(
                        "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                        !selectedWhitelabel &&
                          "tw-bg-accent tw-text-accent-foreground"
                      )}
                    >
                      <Check
                        className={cn(
                          "tw-mr-2 tw-h-4 tw-w-4",
                          !selectedWhitelabel
                            ? "tw-opacity-100"
                            : "tw-opacity-0"
                        )}
                      />
                      {strings("navbar.allWhitelabels")}
                    </button>
                    {filteredWhitelabels.length === 0 ? (
                      <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                        {strings("emptyStates.noWhiteLabels")}
                      </div>
                    ) : (
                      filteredWhitelabels.map((wl) => (
                        <button
                          type="button"
                          key={wl.id}
                          onClick={() => handleWhitelabelSelect(wl)}
                          className={cn(
                            "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                            selectedWhitelabel?.id === wl.id &&
                              "tw-bg-accent tw-text-accent-foreground"
                          )}
                        >
                          <Check
                            className={cn(
                              "tw-mr-2 tw-h-4 tw-w-4 tw-flex-shrink-0",
                              selectedWhitelabel?.id === wl.id
                                ? "tw-opacity-100"
                                : "tw-opacity-0"
                            )}
                          />
                          {wl.icon && (
                            <img
                              src={wl.icon}
                              alt=""
                              className="tw-w-5 tw-h-5 tw-rounded tw-mr-2 tw-flex-shrink-0"
                            />
                          )}
                          {formatWhitelabelName(wl.name)}
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}

          {/* City selector */}
          {showCitySelector && (
            <Popover
              open={cities.length > 0 ? cityDropdownOpen : false}
              onOpenChange={cities.length > 0 ? setCityDropdownOpen : undefined}
            >
              <PopoverTrigger asChild={true}>
                <Button
                  variant="none"
                  size="icon"
                  role="combobox"
                  aria-expanded={cityDropdownOpen}
                  disabled={cities.length === 0}
                  data-tour="city-selector"
                  className={cn(
                    "tw-text-[--navbar-text] focus:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0",
                    "tw-h-11 tw-w-11 tw-rounded-full tw-bg-white/15 tw-border-0",
                    "lg:tw-h-auto lg:tw-w-[180px] lg:tw-px-4 lg:tw-py-2 lg:tw-rounded-lg lg:tw-bg-transparent lg:tw-border lg:tw-border-[--navbar-border] lg:tw-justify-between",
                    citySelectorClassName
                  )}
                  style={
                    {
                      "--navbar-border": `${textColor}40`,
                    } as React.CSSProperties
                  }
                  aria-label={
                    selectedCity
                      ? cityDisplayName(selectedCity)
                      : strings("descriptions.allCities")
                  }
                >
                  {/* Mobile: icon only */}
                  <span className="lg:tw-hidden tw-flex tw-items-center tw-justify-center">
                    <MapPinIcon className="tw-h-4 tw-w-4" />
                  </span>
                  {/* Desktop: full label */}
                  <span className="tw-hidden lg:tw-flex tw-truncate tw-text-base">
                    {selectedCity
                      ? cityDisplayName(selectedCity)
                      : strings("descriptions.allCities")}
                  </span>
                  <ChevronDown className="tw-hidden lg:tw-block tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="tw-w-[calc(100vw-32px)] sm:tw-w-[250px] tw-p-0"
                align="end"
              >
                <div className="tw-flex tw-items-center tw-border-b tw-px-3 tw-py-2">
                  <Search className="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                  <input
                    placeholder={strings("placeholders.searchCities")}
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    className="tw-flex tw-h-8 tw-w-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-muted-foreground"
                  />
                  {citySearch && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="tw-h-6 tw-w-6"
                      onClick={() => setCitySearch("")}
                    >
                      <X className="tw-h-3 tw-w-3" />
                    </Button>
                  )}
                </div>
                <ScrollArea className="tw-h-[300px]">
                  <div className="tw-p-1">
                    <button
                      type="button"
                      onClick={() => handleCitySelect(null)}
                      className={cn(
                        "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                        !selectedCity &&
                          "tw-bg-accent tw-text-accent-foreground"
                      )}
                    >
                      <Check
                        className={cn(
                          "tw-mr-2 tw-h-4 tw-w-4",
                          !selectedCity ? "tw-opacity-100" : "tw-opacity-0"
                        )}
                      />
                      {strings("descriptions.allCities")}
                    </button>
                    {filteredCities.length === 0 ? (
                      <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                        {strings("emptyStates.noCitiesFound")}
                      </div>
                    ) : (
                      filteredCities.map((city) => (
                        <button
                          type="button"
                          key={city.id}
                          onClick={() => handleCitySelect(city)}
                          className={cn(
                            "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                            selectedCity?.id === city.id &&
                              "tw-bg-accent tw-text-accent-foreground"
                          )}
                        >
                          <Check
                            className={cn(
                              "tw-mr-2 tw-h-4 tw-w-4",
                              selectedCity?.id === city.id
                                ? "tw-opacity-100"
                                : "tw-opacity-0"
                            )}
                          />
                          {cityDisplayName(city)}
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}

          {/* Partner selector */}
          {showPartnerSelector && (
            <Popover
              open={partners.length > 0 ? partnerDropdownOpen : false}
              onOpenChange={
                partners.length > 0 ? setPartnerDropdownOpen : undefined
              }
            >
              <PopoverTrigger asChild={true}>
                <Button
                  variant="none"
                  size="icon"
                  role="combobox"
                  aria-expanded={partnerDropdownOpen}
                  disabled={partners.length === 0}
                  data-tour="partner-selector"
                  className={cn(
                    "tw-text-[--navbar-text] focus:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0",
                    "tw-h-11 tw-w-11 tw-rounded-full tw-bg-white/15 tw-border-0",
                    "lg:tw-h-auto lg:tw-w-[180px] lg:tw-px-4 lg:tw-py-2 lg:tw-rounded-lg lg:tw-bg-transparent lg:tw-border lg:tw-border-[--navbar-border] lg:tw-justify-between",
                    partners.length > 0
                      ? "tw-cursor-pointer hover:tw-bg-white/25 lg:hover:tw-bg-white/10"
                      : "tw-opacity-50 tw-cursor-not-allowed"
                  )}
                  style={
                    {
                      "--navbar-border": `${textColor}40`,
                    } as React.CSSProperties
                  }
                  aria-label={
                    selectedPartner?.name || strings("navbar.allPartners")
                  }
                >
                  {/* Mobile: icon only */}
                  <span className="lg:tw-hidden tw-flex tw-items-center tw-justify-center">
                    <Users className="tw-h-4 tw-w-4" />
                  </span>
                  {/* Desktop: full label */}
                  <span className="tw-hidden lg:tw-flex tw-truncate tw-text-base">
                    {selectedPartner?.name || strings("navbar.allPartners")}
                  </span>
                  <ChevronDown className="tw-hidden lg:tw-block tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="tw-w-[calc(100vw-32px)] sm:tw-w-[250px] tw-p-0"
                align="end"
              >
                <div className="tw-flex tw-items-center tw-border-b tw-px-3 tw-py-2">
                  <Search className="tw-mr-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                  <input
                    placeholder={strings("partners.searchPartners")}
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    className="tw-flex tw-h-8 tw-w-full tw-bg-transparent tw-text-sm tw-outline-none placeholder:tw-text-muted-foreground"
                  />
                  {partnerSearch && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="tw-h-6 tw-w-6"
                      onClick={() => setPartnerSearch("")}
                    >
                      <X className="tw-h-3 tw-w-3" />
                    </Button>
                  )}
                </div>
                <ScrollArea className="tw-h-[300px]">
                  <div className="tw-p-1">
                    <button
                      type="button"
                      onClick={() => handlePartnerSelect(null)}
                      className={cn(
                        "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                        !selectedPartner &&
                          "tw-bg-accent tw-text-accent-foreground"
                      )}
                    >
                      <Check
                        className={cn(
                          "tw-mr-2 tw-h-4 tw-w-4",
                          !selectedPartner ? "tw-opacity-100" : "tw-opacity-0"
                        )}
                      />
                      {strings("navbar.allPartners")}
                    </button>
                    {filteredPartners.length === 0 ? (
                      <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
                        {strings("partners.emptyTitle")}
                      </div>
                    ) : (
                      filteredPartners.map((partner) => (
                        <button
                          type="button"
                          key={partner.id}
                          onClick={() => handlePartnerSelect(partner)}
                          className={cn(
                            "tw-relative tw-flex tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-rounded-sm tw-py-2 tw-px-3 tw-text-sm tw-outline-none hover:tw-bg-accent hover:tw-text-accent-foreground",
                            selectedPartner?.id === partner.id &&
                              "tw-bg-accent tw-text-accent-foreground"
                          )}
                        >
                          <Check
                            className={cn(
                              "tw-mr-2 tw-h-4 tw-w-4",
                              selectedPartner?.id === partner.id
                                ? "tw-opacity-100"
                                : "tw-opacity-0"
                            )}
                          />
                          {partner.name}
                        </button>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
              <Button
                variant="none"
                data-tour="user-menu"
                className="tw-flex tw-items-center tw-gap-1.5 tw-p-1 tw-cursor-pointer hover:tw-bg-white/10 tw-text-[--navbar-text] focus:tw-outline-none focus-visible:tw-ring-0 focus-visible:tw-ring-offset-0"
              >
                <Avatar className="tw-h-8 tw-w-8 tw-border tw-border-white/30">
                  <AvatarFallback className="tw-text-xs tw-font-medium tw-h-full tw-w-full tw-bg-white/15 tw-text-[--navbar-text]">
                    {getInitials(email)}
                  </AvatarFallback>
                </Avatar>
                <ChevronDown className="tw-h-3 tw-w-3 tw-opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="tw-w-56 tw-p-1.5">
              <div className="tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-foreground tw-truncate">
                {email}
              </div>
              <DropdownMenuSeparator className="tw-my-1.5" />

              {onHelpClick && (
                <DropdownMenuItem
                  onClick={onHelpClick}
                  className="tw-py-2.5 tw-px-3 tw-cursor-pointer tw-rounded-md hover:tw-bg-accent focus:tw-bg-accent"
                >
                  <HelpCircle className="tw-mr-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                  <span>{strings("navbar.helpSupport")}</span>
                </DropdownMenuItem>
              )}

              {onTourClick && (
                <DropdownMenuItem
                  onClick={onTourClick}
                  className="tw-py-2.5 tw-px-3 tw-cursor-pointer tw-rounded-md hover:tw-bg-accent focus:tw-bg-accent"
                >
                  <Compass className="tw-mr-3 tw-h-4 tw-w-4 tw-text-cyan-500" />
                  <span>{strings("navbar.startTour")}</span>
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator className="tw-my-1.5" />

              {onSettingsClick && (
                <DropdownMenuItem
                  onClick={onSettingsClick}
                  className="tw-py-2.5 tw-px-3 tw-cursor-pointer tw-rounded-md hover:tw-bg-accent focus:tw-bg-accent"
                >
                  <Settings className="tw-mr-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                  <span>{strings("navbar.settings")}</span>
                </DropdownMenuItem>
              )}
              {onVersionToggle && (
                <DropdownMenuItem
                  onClick={onVersionToggle}
                  className="tw-py-2.5 tw-px-3 tw-cursor-pointer tw-rounded-md hover:tw-bg-accent focus:tw-bg-accent"
                >
                  <ArrowLeftRight className="tw-mr-3 tw-h-4 tw-w-4 tw-text-muted-foreground" />
                  <span>{versionLabel || strings("navbar.switchVersion")}</span>
                </DropdownMenuItem>
              )}
              {onLogout && (
                <DropdownMenuItem
                  onClick={onLogout}
                  className="tw-py-2.5 tw-px-3 tw-cursor-pointer tw-rounded-md hover:tw-bg-destructive/10 focus:tw-bg-destructive/10"
                >
                  <LogOut className="tw-mr-3 tw-h-4 tw-w-4 tw-text-destructive" />
                  <span className="tw-text-destructive">
                    {strings("descriptions.logOut")}
                  </span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    );
  }
);

Navbar.displayName = "Navbar";

export { Navbar };
